/**
 * API utilities for handling authentication headers and common API operations.
 */

import { isAuthenticated, getUserEmail } from '@/auth/Auth0Client';

const STORAGE_KEY = 'anonymous-user-id';

/**
 * Get a unique anonymous user ID from localStorage, creating one if it doesn't exist.
 */
function getAnonymousUserId(): string {
    let anonymousId = localStorage.getItem(STORAGE_KEY);

    if (!anonymousId) {
        // Generate a UUID v4 without dashes
        const uuid = crypto.randomUUID().replace(/-/g, '');
        anonymousId = `anon-${uuid}`;
        localStorage.setItem(STORAGE_KEY, anonymousId);
    }

    return anonymousId;
}

/**
 * Clear the anonymous user ID from localStorage.
 * Should be called after successful authentication.
 */
export function clearAnonymousUserId(): void {
    localStorage.removeItem(STORAGE_KEY);
}

/**
 * Build headers for API requests, including authentication headers when available.
 *
 * For authenticated users: adds X-User-Email header with user's email
 * For unauthenticated users: adds X-Anonymous-User-ID header
 *
 * @param additionalHeaders - Optional additional headers to merge
 * @returns Promise resolving to headers object
 */
export async function buildApiHeaders(
    additionalHeaders: Record<string, string> = {}
): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...additionalHeaders,
    };

    try {
        // Check if user is authenticated
        const authenticated = await isAuthenticated();

        if (authenticated) {
            // User is authenticated - add user email header
            const email = await getUserEmail();
            if (email) {
                headers['X-User-Email'] = email;
            } else {
                // If no email, fall back to anonymous
                console.warn('Authenticated user has no email, using anonymous ID');
                headers['X-Anonymous-User-ID'] = getAnonymousUserId();
            }
        } else {
            // User is not authenticated - add anonymous user ID
            headers['X-Anonymous-User-ID'] = getAnonymousUserId();
        }
    } catch (error) {
        // If there's any error getting auth status, treat as anonymous
        console.error('Error building auth headers, using anonymous ID:', error);
        headers['X-Anonymous-User-ID'] = getAnonymousUserId();
    }

    return headers;
}
